/* eslint-disable jira/filename-case */
import React, { useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { COMPOUND_OPERATOR_AND, OPERATOR_EQUALS, creators, print } from '@atlaskit/jql-ast';
import { ComponentsInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-components-inline-edit-full/src/ui/components/index.tsx';
import { escapeBackslashes } from '../../../utils/index.tsx';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import type { ComponentsCell_ComponentsInlineEditCell$key } from './__generated__/ComponentsCell_ComponentsInlineEditCell.graphql';

export type ComponentsInlineEditCellProps = {
	fieldRef: ComponentsCell_ComponentsInlineEditCell$key;
	issueSearchBaseUrl: string;
	jqlTerm: string;
	projectKey: string;
};

/**
 * Expected JQL Output: `project = ${projectKey} AND ${jqlTerm} = ${name}`
 */
const getLinkJql = (projectKey: string, jqlTerm: string, name: string) => {
	const jast = creators.jast(
		creators.query(
			creators.compoundClause(creators.compoundOperator(COMPOUND_OPERATOR_AND), [
				creators.terminalClause(
					creators.field('project'),
					creators.operator(OPERATOR_EQUALS),
					creators.valueOperand(projectKey),
				),
				creators.terminalClause(
					creators.field(escapeBackslashes(jqlTerm)),
					creators.operator(OPERATOR_EQUALS),
					creators.valueOperand(escapeBackslashes(name)),
				),
			]),
		),
	);

	return print(jast, {
		operatorCase: 'upper',
	});
};

export const ComponentsInlineEditCell = ({
	fieldRef,
	issueSearchBaseUrl,
	jqlTerm,
	projectKey,
}: ComponentsInlineEditCellProps) => {
	const data = useFragment<ComponentsCell_ComponentsInlineEditCell$key>(
		graphql`
			fragment ComponentsCell_ComponentsInlineEditCell on JiraComponentsField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...components_issueFieldComponentsInlineEditFull_ComponentsInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data.id, isEditable);

	const formatComponentJql = useCallback(
		(componentName: string) => getLinkJql(projectKey, jqlTerm, componentName),
		[jqlTerm, projectKey],
	);

	return (
		<ComponentsInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			baseSearchUrl={issueSearchBaseUrl}
			editViewPopup
			editViewPopupAlignBlock="center"
			formatComponentJql={formatComponentJql}
			fragmentRef={data}
			isTruncated
			isEditable={isEditable ?? false}
			menuPosition="fixed"
			readViewFitContainerHeight
			isTruncatedWithHoverPopover={isSingleLineRowHeightEnabled}
		/>
	);
};
